/**
 * Módulo del tutorial: Tu equipo y sus permisos (KAN-232).
 *
 * Para el dueño/administrador: invitar de verdad (email → rol → enviar, cada
 * campo señalado y el botón que avanza), los 5 roles con la ayuda en vivo,
 * la tabla de Miembros (rol/suspender/eliminar — sólo el dueño cambia roles)
 * y qué le llega al invitado + cancelar pendientes.
 *
 * Anclas: menu-admin-usuarios (menú Sistema → Usuarios),
 * usuarios-invitar-email / usuarios-invitar-rol / usuarios-btn-invitar,
 * usuarios-role-help, usuarios-miembros. Permiso del módulo: admin.view.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

export const usuariosModule: TutorialModuleDef = {
  id: 'usuarios',
  title: 'Tu equipo y sus permisos',
  description: 'Invitá a tu gente de verdad — email, rol, enviar — y administrá miembros: quién ve, quién escribe, quién se suspende.',
  emoji: '👥',
  image: 'images/tutorial/usuarios.png',
  permission: { module: 'admin', action: 'view' },
  grupo: 'equipo',
  route: '/admin/usuarios',
  steps: [
    {
      id: 'concepto-usuarios',
      route: '/tutorial',
      text: 'El campo es trabajo de varios: acá invitás a tu gente con su rol. Cada uno ve lo que le toca y escribe lo que puede — el dueño manda, el operador carga.',
      narration:
        'El campo nunca es de uno solo: el que está en la manga, el que administra, el contador. En esta sección invitas a tu gente a la app, cada uno con su rol. Y el rol no es decorativo: define qué ve y qué puede tocar cada uno. El orden en los números empieza por el orden en los permisos.',
    },
    {
      id: 'ir-a-usuarios',
      target: '[data-ar-guide="menu-admin-usuarios"]',
      label: 'Usuarios (menú Sistema)',
      text: 'En el menú, sección Sistema, tocá "Usuarios" (en celular: menú ☰).',
      narration:
        'Andá al menú, sección Sistema, y tocá Usuarios. Ahí está tu equipo: quién tiene acceso, con qué rol, y el lugar para sumar a alguien nuevo.',
    },
    {
      id: 'invitar-email',
      route: '/admin/usuarios',
      target: '[data-ar-guide="usuarios-invitar-email"]',
      label: 'Email del invitado',
      advanceOnClick: false,
      text: 'Vamos a invitar de verdad. Poné acá el email de la persona — le va a llegar la invitación a esa dirección.',
      narration:
        'Manos a la obra: invitemos a alguien. En este casillero va el correo de la persona, bien escrito, que a esa dirección le va a llegar la invitación.',
    },
    {
      id: 'invitar-rol',
      route: '/admin/usuarios',
      target: '[data-ar-guide="usuarios-invitar-rol"]',
      extraTargets: ['[data-ar-guide="usuarios-role-help"]'],
      label: 'Rol (y la ayuda al lado)',
      advanceOnClick: false,
      text: 'Elegí el rol: Dueño y Administrador hacen todo; el Gerente gestiona sin tocar lo sensible; el Operador carga el día a día; Solo lectura sólo mira. La app te muestra al lado qué puede hacer cada uno.',
      narration:
        'Ahora el rol, y éste sí que importa: define qué ve y qué toca. El Dueño y el Administrador pueden todo. El Gerente administra el día a día sin tocar las cosas de fondo. El Operador es el de la manga: carga animales, pesajes, lluvias, pero no borra ni administra. Y Solo lectura, para el que sólo tiene que mirar los números. ¿No te acordás qué puede cada uno? Mirá la ayuda de acá al lado: la app te lo explica mientras elegís.',
    },
    {
      id: 'enviar-invitacion',
      route: '/admin/usuarios',
      target: '[data-ar-guide="usuarios-btn-invitar"]',
      label: 'Botón Invitar',
      text: 'Tocá "Invitar" — le llega un mail, lo acepta y entra a TU campo con su cuenta. Sin compartir tu contraseña nunca más.',
      narration:
        'Y apretá Invitar. A la persona le llega un correo, lo acepta, y entra a trabajar en tu campo con su propia cuenta. Chau con pasarle la contraseña a todo el mundo: cada uno entra con lo suyo, y vos lo podés sacar cuando quieras.',
    },
    {
      id: 'roles',
      route: '/admin/usuarios',
      text: 'Con el tiempo, alguien necesita más o menos permiso: desde la tabla de acá abajo le cambiás el rol — pero ojo: LOS ROLES SÓLO LOS CAMBIA EL DUEÑO (si sos administrador, vas a ver los selectores grises).',
      narration:
        'Con el tiempo, alguien necesita más o menos permiso. Eso se cambia acá abajo, en la tabla de miembros. Pero un detalle importante: los roles sólo los cambia el dueño del campo. Si sos administrador, vas a ver los selectores bloqueaditos, en gris: podés mirar cómo está cada uno, pero el reparto de permisos lo maneja el dueño.',
    },
    {
      id: 'miembros',
      route: '/admin/usuarios',
      target: '[data-ar-guide="usuarios-miembros"]',
      label: 'Tabla de Miembros',
      advanceOnClick: false,
      text: 'Acá está tu equipo: el rol de cada uno, y por fila podés SUSPENDER (acceso pausado, vuelve cuando querés) o ELIMINAR (fuera del campo) — menos el dueño, intocable.',
      narration:
        'Y ésta es la lista de tu equipo, miembro por miembro. Al lado de cada uno, dos botones bien distintos: Suspender, que le pausa el acceso, aunque sea de gira, y cuando vuelve lo activás de nuevo; y Eliminar, que lo saca del campo de una vez. Al dueño no lo toca nadie, claro: el que manda, manda.',
    },
    {
      id: 'invitado',
      route: '/admin/usuarios',
      text: '¿Invitaste y te arrepentiste, o el mail nunca llegó? Abajo aparece "Invitaciones pendientes": ahí la CANCELÁS, o reenviás la invitación cuando quieras.',
      narration:
        '¿Y si invitaste a alguien y te arrepentís, o el correo nunca llegó? Fijate abajo del todo: si hay invitaciones pendientes, aparecen ahí listadas, con su fecha de vencimiento, y las podés cancelar con un toque. Nada queda flotando suelto: cada invitación está a la vista hasta que se acepta o la cancelás.',
    },
    {
      id: 'cierre-usuarios',
      route: '/admin/usuarios',
      text: 'Con el equipo cargando cada uno desde su lugar, el campo se actualiza solo — y vos ves todo. ¡Cualquier cosa, la vaquita 🐄 siempre está!',
      narration:
        'Con tu gente cargando cada uno desde su lugar, el campo se actualiza solo, y vos, desde arriba, lo ves todo. Ya casi recorrés la app entera, criollo: te queda un módulo nomás. Y para cualquier duda, acordate: la vaquita que flota en la esquina siempre está esperando tu pregunta.',
    },
  ],
  completionCheck: async () => false,
};
